import React from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import UrgencyBanner from '../components/UrgencyBanner';
import { contactInfo } from '../data/mock';
import { Phone, MessageCircle, Heart, Clock, Shield, Star, CheckCircle, ArrowRight, Zap } from 'lucide-react';

const FaireRevenirSonEx = () => {
  return (
    <div className="bg-black min-h-screen">
      <SEOHead 
        title="Faire Revenir son Ex en 24-72h | Rituel Puissant | Grand Marabout Africain"
        description="Comment faire revenir son ex rapidement ? Grand marabout africain expert en retour affectif. Faites revenir votre ex en 24 à 72 heures avec nos rituels puissants. Magie blanche, noire, rouge. Résultats garantis !"
        keywords="faire revenir son ex, faire revenir son ex en 24h, faire revenir son ex en 72h, faire revenir son ex rapidement, comment faire revenir son ex, rituel pour faire revenir son ex, marabout faire revenir ex, retour affectif"
        canonical="https://retour-affectif-rapide.com/faire-revenir-son-ex"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center justify-center py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
          
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <span className="inline-block px-6 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-500 text-sm font-semibold tracking-wide mb-8">
              Rituel de Retour Affectif Puissant
            </span>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              <span className="text-amber-500">Faire Revenir son Ex</span>
              <br />
              <span className="text-3xl sm:text-4xl md:text-5xl">Rapidement et Définitivement</span>
            </h1>
            
            <p className="text-gray-300 text-lg sm:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
              Vous voulez <strong>faire revenir votre ex</strong> ? <strong className="text-amber-500">Maître Diamma</strong>, 
              grand marabout africain, réalise des rituels puissants pour faire revenir l'être aimé. 
              <strong className="text-amber-500"> Résultats visibles en 24 à 72 heures</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                Faire revenir mon ex
              </a>
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-amber-500 text-amber-500 font-bold text-lg rounded-full hover:bg-amber-500 hover:text-black transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-3" />
                {contactInfo.phone}
              </a>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center"><Zap className="w-4 h-4 mr-2 text-amber-500" />Rituels puissants</span>
              <span className="flex items-center"><Clock className="w-4 h-4 mr-2 text-amber-500" />24-72 heures</span>
              <span className="flex items-center"><Shield className="w-4 h-4 mr-2 text-blue-500" />100% confidentiel</span>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900/50 border border-amber-500/20 rounded-2xl p-8 sm:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
                Comment <span className="text-amber-500">Faire Revenir son Ex</span> ?
              </h2>
              
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  <strong className="text-white">Faire revenir son ex</strong> est le souhait de nombreuses personnes après une rupture. 
                  La douleur de la séparation est immense, mais il existe des solutions pour <strong>ramener l'être aimé</strong>. 
                  Les <strong className="text-amber-500">rituels de magie ancestrale africaine</strong> sont reconnus pour leur efficacité.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Les rituels pour faire revenir son ex</h3>
                <p>
                  Plusieurs types de rituels peuvent être utilisés pour <strong>faire revenir votre ex</strong> :
                </p>
                <ul className="list-disc list-inside space-y-3 ml-4">
                  <li><strong className="text-white">Magie blanche</strong> : Douce et respectueuse, elle attire les énergies positives pour raviver l'amour</li>
                  <li><strong className="text-white">Magie rouge</strong> : Spéciale pour l'amour et la passion, elle intensifie les sentiments</li>
                  <li><strong className="text-white">Magie noire</strong> : Plus puissante, pour les situations complexes nécessitant une action rapide</li>
                </ul>
                
                <h3 className="text-2xl font-bold text-white mt-8">Pourquoi faire appel à un marabout ?</h3>
                <p>
                  Un <strong>grand marabout africain</strong> possède le savoir ancestral nécessaire pour réaliser des rituels efficaces. 
                  Avec plus de 30 ans d'expérience, les techniques transmises de génération en génération permettent d'obtenir 
                  des résultats là où d'autres méthodes ont échoué. <strong className="text-amber-500">Faire revenir son ex</strong> devient possible.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Résultats rapides garantis</h3>
                <p>
                  Contrairement aux méthodes traditionnelles qui prennent des mois, nos rituels pour <strong>faire revenir son ex</strong> 
                  produisent des effets en <strong className="text-amber-500">24 à 72 heures</strong>. Votre ex ressentira un besoin 
                  irrésistible de vous recontacter et de revenir vers vous.
                </p>
                
                <div className="grid gap-4 mt-8">
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Mon ex est revenu après 48h. Je n'y croyais plus mais le rituel a fonctionné !"</p>
                    <p className="text-amber-500 mt-2 text-sm">- Sophie, Bruxelles</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Il m'a appelé 3 jours après le rituel comme si rien ne s'était passé. Incroyable !"</p>
                    <p className="text-amber-500 mt-2 text-sm">- Camille, Genève</p>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className="mt-12 p-6 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
                <p className="text-white font-bold text-xl mb-4">
                  Prêt(e) à faire revenir votre ex ?
                </p>
                <p className="text-gray-300 mb-6">
                  Consultation confidentielle et personnalisée
                </p>
                <a
                  href={contactInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-3" />
                  Faire revenir mon ex maintenant
                  <ArrowRight className="w-5 h-5 ml-3" />
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center">
              Questions sur <span className="text-amber-500">Faire Revenir son Ex</span>
            </h2>
            
            <div className="space-y-6">
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Comment faire revenir son ex qui ne veut plus me parler ?</h3>
                <p className="text-gray-300">Les rituels de retour affectif agissent sur les énergies spirituelles. Même si votre ex refuse tout contact, les rituels peuvent lever ces blocages et raviver ses sentiments pour vous.</p>
              </div>
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Les rituels pour faire revenir son ex fonctionnent-ils à distance ?</h3>
                <p className="text-gray-300">Oui, absolument. L'énergie spirituelle ne connaît pas de frontières. Que vous soyez en France, Belgique, Suisse, Canada ou ailleurs, les rituels sont tout aussi efficaces.</p>
              </div>
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">En combien de temps mon ex va-t-il revenir ?</h3>
                <p className="text-gray-300">Les premiers signes apparaissent généralement entre 24 et 72 heures. Votre ex commencera à penser à vous et ressentira le besoin de vous recontacter.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <FloatingWhatsApp />
      <UrgencyBanner />
    </div>
  );
};

export default FaireRevenirSonEx;
